import type { ChangeEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Callout } from "ancestry-ui-react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import styles from "./BirthYearCalculator.module.scss";

export const MAX_AGE = 120;
export const MIN_YEAR = 1000;
export const CURRENT_YEAR = new Date().getFullYear();

const ALLOWED_NUMBER_INPUT_KEYS = new Set([
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "Enter",
    "Tab",
]);

interface BirthYearCalculatorProps {
    onSubmit: (calculatedYear: number) => void;
    label?: string;
    /** When true, renders the calculator form inline without trigger button and Callout wrapper */
    inline?: boolean;
    /** Overrides the default "aboutLabel" text (e.g. "Jane was about") */
    aboutText?: string;
    /** Initial value for the age input (used to restore state after remount) */
    initialAge?: string;
    /** Initial value for the year input (used to restore state after remount) */
    initialYear?: string;
    /** Called when age or year input values change (used to persist state externally) */
    onValuesChange?: (age: string, year: string) => void;
}

export const BirthYearCalculator: React.FC<BirthYearCalculatorProps> = ({
    onSubmit,
    label,
    inline = false,
    aboutText,
    initialAge = "",
    initialYear = "",
    onValuesChange,
}: BirthYearCalculatorProps) => {
    const [isOpen, setIsOpen] = useState(inline);
    const { t } = useTranslation();

    const close = useCallback(() => {
        if (!inline) {
            setIsOpen(false);
        }
    }, [inline]);

    const toggle = useCallback(() => {
        if (!inline) {
            setIsOpen((prevIsOpen) => !prevIsOpen);
        }
    }, [inline]);

    const alertId = useId();

    const calloutRef = useRef<HTMLButtonElement>(null);
    const ageRef = useRef<HTMLInputElement>(null);
    const yearRef = useRef<HTMLInputElement>(null);
    const repositionCallout = useRef<() => void>();

    const [showWarning, setShowWarning] = useState(false);
    const [approximateAge, setApproximateAge] = useState(initialAge);
    const [approximateYear, setApproximateYear] = useState(initialYear);

    useEffect(() => {
        if (showWarning && typeof repositionCallout.current === "function") {
            repositionCallout.current();
        }
    }, [showWarning]);

    const handleAge = (event: ChangeEvent<HTMLInputElement>): void => {
        if (showWarning) {
            setShowWarning(false);
        }

        const age = event.target.value;
        const approxAge = age.slice(0, 3);
        setApproximateAge(approxAge);
        onValuesChange?.(approxAge, approximateYear);

        if (parseInt(approxAge, 10) > MAX_AGE) {
            setShowWarning(true);
        }
    };

    const handleApproximateYear = (event: ChangeEvent<HTMLInputElement>): void => {
        if (showWarning) {
            setShowWarning(false);
        }

        const year = event.target.value;
        const approxYear = year.slice(0, 4);
        setApproximateYear(approxYear);
        onValuesChange?.(approximateAge, approxYear);
    };

    const hasInvalidValues = (approxAge: string, approxYear: string): boolean => {
        const yearWithoutStartingZeroes = approxYear.replace(/^[0]+/g, "");
        const ageWithoutStartingZeroes = approxAge.replace(/^[0]+/g, "");
        const hasEmptyValues = [yearWithoutStartingZeroes, ageWithoutStartingZeroes].includes("");
        const hasInvalidYear = yearWithoutStartingZeroes.length !== 4;
        const isAgeExceed120 = parseInt(approxAge, 10) > MAX_AGE;
        const isYearLessThan1000 = parseInt(approxYear, 10) <= MIN_YEAR;
        const isYearGreaterThanCurrent = parseInt(approxYear, 10) > CURRENT_YEAR;

        return hasEmptyValues || hasInvalidYear || isAgeExceed120 || isYearLessThan1000 || isYearGreaterThanCurrent;
    };

    const handleCalculateClick = (): void => {
        if (approximateAge === "" && approximateYear === "") {
            close();
            return;
        }

        if (hasInvalidValues(approximateAge, approximateYear)) {
            setShowWarning(true);
            return;
        }

        const birthYear = Number(approximateYear) - Number(approximateAge);
        onSubmit(birthYear);
        close();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === "Enter") {
            handleCalculateClick();
            event.preventDefault();
        }
        if (!ALLOWED_NUMBER_INPUT_KEYS.has(event.key)) {
            event.preventDefault();
        }
    };

    const calculatorForm = (
        <div className={styles.yearCalculatorForm}>
            <div className={styles.yearCalculatorFields}>
                <span>{aboutText || t("birthYearCalculator.aboutLabel")}</span>
                <div>
                    <input
                        className={styles.input}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={MAX_AGE}
                        value={approximateAge}
                        onChange={handleAge}
                        onKeyDown={handleKeyDown}
                        ref={ageRef}
                        aria-label={t("birthYearCalculator.ageInputAriaLabel")}
                        placeholder={t("birthYearCalculator.agePlaceholder")}
                        aria-describedby={showWarning ? alertId : undefined}
                    />
                </div>
                <span>{t("birthYearCalculator.yearsOldInLabel")}</span>
                <div>
                    <input
                        className={styles.input}
                        type="number"
                        inputMode="numeric"
                        min={MIN_YEAR}
                        max={CURRENT_YEAR}
                        value={approximateYear}
                        onChange={handleApproximateYear}
                        onKeyDown={handleKeyDown}
                        ref={yearRef}
                        aria-label={t("birthYearCalculator.yearsOldInLabel")}
                        placeholder={t("birthYearCalculator.yearPlaceholder")}
                        aria-describedby={showWarning ? alertId : undefined}
                    />
                </div>
            </div>
            <div>
                <button
                    type="button"
                    className={classNames("ancBtn", "green", "sml", {
                        disabled: !approximateAge || !approximateYear,
                    })}
                    aria-label={t("birthYearCalculator.calculateButton")}
                    onClick={handleCalculateClick}
                >
                    {t("birthYearCalculator.calculateButton")}
                </button>
            </div>
            <div>
                {showWarning && (
                    <div id={alertId} className="alert warning" data-testid="calloutError" role="alert">
                        {t("birthYearCalculator.validationErrorMessage")}
                    </div>
                )}
            </div>
        </div>
    );

    if (inline) {
        return calculatorForm;
    }

    return (
        <>
            <button
                className={classNames(`link iconAfter`, {
                    iconArrowSmallUpAfter: isOpen,
                    iconArrowSmallDownAfter: !isOpen,
                })}
                ref={calloutRef}
                type="button"
                aria-label={t("birthYearCalculator.calculatorBtnAriaLabel")}
            >
                <span className="icon iconCalculator" />
                {label && <span>{label}</span>}
            </button>
            <Callout
                id="testCallout"
                trigger={calloutRef}
                onRequestAction={toggle}
                open={isOpen}
                onAfterOpen={() => ageRef.current?.focus()}
                classes={styles.callout}
                reposition={(method: () => void) => {
                    repositionCallout.current = method;
                }}
            >
                {calculatorForm}
            </Callout>
        </>
    );
};
